import React, { useEffect, memo, useRef } from 'react';
import { connect } from 'react-redux';
import { selectCurrentDisplayedIncident } from '../../../../state/modules/incidents/selectors';
import WaitingForIncidentMessage from '../shared/IncidentWidgets/WaitingForIncidentMessage';
import ElapsedTime from './ElapsedTime';
import PlaylistAudioPlayer from '../../../../utils/PlaylistAudioPlayer';
import Settings from './Settings';
import PrioBadge from '../../../ui-kit/PrioBadge';
import moment from 'moment';

const addressStyle = {
  width: 'calc(100% - 280px)'
};

// To test in a clean environment:
// station = FactoryBot.create(:station, default_alerting_tone: 'minions')
// dashboard = Dashboards::Dashboard.build(station)
// dashboard_page = dashboard.dashboard_pages.find_by(page_type: 'incident')
// FactoryBot.create(:widget, archetype_id: 'incident_message', dashboard_page:,
//                      settings: { textToSpeech: false, textToSpeechRepeatCount: 2 })
// Launchy.open("http://localhost:3000/groups/#{station.id}/dashboards/#{dashboard.id}")
// FactoryBot.create(:incident, groups: [station])

const AlertSoundPlayer = memo(({ alertToneFile, messageToSpeechFile, repeatCount, play }) => {
  // create a playlist with the alert tone followed by repeatCount times the
  // text to speech message if messageToSpeechFile is provided, otherwise just
  // the alert tone
  if (!messageToSpeechFile) repeatCount = 0;
  const messageToSpeechEntries = Array.from({ length: repeatCount }, () => messageToSpeechFile);
  const playlist = [alertToneFile, ...messageToSpeechEntries];

  return <PlaylistAudioPlayer playlist={playlist} play={play} />;
});
AlertSoundPlayer.displayName = 'AlertSoundPlayer';

const IncidentMessage = ({ displayedIncident, textToSpeech, timeLimit, timeLimitCheck, textToSpeechRepeatCount }) => {
  const spokenIncidentIds = useRef(new Set());
  const previousIncidentId = useRef(null);

  // When the currently displayed incident changes (i.e. there is more than 1
  // incident), mark the incident as spoken. This is to prevent incidents from
  // being spoken repeatedly, causing annoyance with users.
  useEffect(() => {
    if (!displayedIncident) return;
    if (displayedIncident.id === previousIncidentId.current) return;
    // adds the incident id to the spoken incident ids, to prevent playing the
    // sound everytime the incident becomes active
    if (previousIncidentId.current && !spokenIncidentIds.current.has(previousIncidentId.current)) {
      spokenIncidentIds.current.add(previousIncidentId.current);
    }
    previousIncidentId.current = displayedIncident.id;
  }, [displayedIncident]);

  if (!displayedIncident) return <WaitingForIncidentMessage />;

  // desired behaviour for audio playback:
  // Play alert sound, once it finishes, play text to speech if setting is
  // enabled.
  // If loop is enabled, replay text to speech a couple of times if there are
  // multiple active incidents, only the first time it's shown should emit the
  // sound.
  const hasIncidentBeenSpoken = spokenIncidentIds.current.has(displayedIncident.id);
  const shouldPlaySound = !hasIncidentBeenSpoken;
  const timeLimitBoundary = moment(displayedIncident.created_at).add(timeLimit, 'seconds');
  const effectiveTimeLimit = timeLimitCheck ? timeLimitBoundary : null;

  const messageToSpeechFile = textToSpeech ? displayedIncident.message_to_speech_url : null;

  return (
    <>
      <AlertSoundPlayer
        play={shouldPlaySound}
        key={displayedIncident.message_to_speech_url}
        messageToSpeechFile={messageToSpeechFile}
        alertToneFile={displayedIncident.alert_tone_url}
        repeatCount={textToSpeechRepeatCount}
      />
      <ElapsedTime
        startTime={displayedIncident.created_at}
        timeLimit={effectiveTimeLimit}
        incidentId={displayedIncident?.id}
      />
      <h2 className="fw-bold">
        <PrioBadge prio={displayedIncident.prio} /> {displayedIncident.body}
      </h2>
      {displayedIncident.address && <h3 style={addressStyle}>{displayedIncident.address.formatted_address}</h3>}
    </>
  );
};

const mapStateToProps = state => ({
  displayedIncident: selectCurrentDisplayedIncident(state)
});

const IncidentMessageMemo = memo(IncidentMessage);

Object.assign(IncidentMessageMemo, {
  SettingsContent: Settings,
  defaultSettings: {
    textToSpeech: false,
    textToSpeechRepeatCount: 1,
    timeLimitCheck: false,
    timeLimit: 300 // 5 minutes default
  }
});

export default connect(mapStateToProps)(IncidentMessageMemo);
